import type { PresentationData, SlideData } from '../types'
import html2canvas from 'html2canvas'

/**
 * Presentation Export Utilities
 *
 * Captures slides by creating a hidden rendering environment
 * and using html2canvas to capture each slide as an image.
 */

/**
 * Create a temporary export container for rendering all slides
 */
function createExportContainer(): HTMLElement {
  const container = document.createElement('div')
  container.id = 'presentation-export-container'
  container.style.cssText = `
    position: fixed;
    top: 0;
    left: -9999px;
    width: 1920px;
    height: 1080px;
    z-index: -9999;
    overflow: hidden;
  `
  document.body.appendChild(container)
  return container
}

/**
 * Render a slide's content to HTML for export
 */
function renderSlideForExport(slide: SlideData): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.style.cssText = `
    width: 1920px;
    height: 1080px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  `

  // Get background style
  const background = slide.background || 'white'
  const backgroundStyles: Record<string, string> = {
    'gradient-primary': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'gradient-secondary': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'gradient-accent': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'gradient-dark': 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    'white': '#ffffff',
    'gray': '#f1f5f9',
  }
  const bgStyle = backgroundStyles[background] || background
  wrapper.style.background = bgStyle

  // Determine text color
  const isDark = background === 'gradient-dark' || background.startsWith('#0') || background.startsWith('#1')
  const textColor = isDark ? '#ffffff' : '#1e293b'
  const subtitleColor = isDark ? 'rgba(255,255,255,0.7)' : '#64748b'

  // Content wrapper
  const contentWrapper = document.createElement('div')
  contentWrapper.style.cssText = `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    max-width: 1600px;
    max-height: 900px;
    overflow: hidden;
    padding: 60px;
  `

  // Check if this is a custom component slide
  if (slide.component) {
    contentWrapper.innerHTML = `
      <div style="color: ${textColor}; font-size: 24px; padding: 40px; text-align: center;">
        <h2 style="font-size: 48px; font-weight: bold; margin-bottom: 24px;">${escapeHtml(slide.title || 'Custom Component Slide')}</h2>
        ${slide.subtitle ? `<p style="font-size: 32px; color: ${subtitleColor}; margin-bottom: 32px;">${escapeHtml(slide.subtitle)}</p>` : ''}
        <div style="background: rgba(255,255,255,0.1); padding: 20px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.2);">
          <p style="margin: 0;">This slide uses a custom React component.</p>
          <p style="margin: 10px 0 0 0; font-size: 18px; opacity: 0.8;">For full export, please use the browser's print function (Ctrl/Cmd + P) or take screenshots manually.</p>
        </div>
      </div>
    `
  } else {
    // Standard slide rendering
    let html = ''

    if (slide.title) {
      html += `<h1 style="font-size: 64px; font-weight: bold; margin-bottom: 24px; color: ${textColor}; line-height: 1.2; max-width: 1400px;">${escapeHtml(slide.title)}</h1>`
    }

    if (slide.subtitle) {
      html += `<h2 style="font-size: 40px; font-weight: 300; margin-bottom: 40px; color: ${subtitleColor}; max-width: 1200px;">${escapeHtml(slide.subtitle)}</h2>`
    }

    if (slide.content) {
      const contentText = renderContentToString(slide.content)
      html += `<div style="font-size: 32px; color: ${textColor}; line-height: 1.6; max-width: 1100px;">${contentText}</div>`
    }

    contentWrapper.innerHTML = html
  }

  wrapper.appendChild(contentWrapper)
  return wrapper
}

/**
 * Convert React content to HTML string
 */
function renderContentToString(content: React.ReactNode): string {
  if (typeof content === 'string' || typeof content === 'number') {
    return escapeHtml(String(content))
  }
  if (Array.isArray(content)) {
    return content.map(renderContentToString).filter(Boolean).join('<br>')
  }
  if (content && typeof content === 'object') {
    // Try to extract text from React elements
    // @ts-ignore
    if (content.props?.children) {
      // @ts-ignore
      return renderContentToString(content.props.children)
    }
  }
  return ''
}

/**
 * Capture a slide element as an image using html2canvas
 */
async function captureSlideElement(element: HTMLElement): Promise<string> {
  // Wait a bit for any styles to apply
  await new Promise(resolve => setTimeout(resolve, 50))

  const canvas = await html2canvas(element, {
    useCORS: true,
    allowTaint: true,
    backgroundColor: null,
    logging: false,
    scale: 2, // Higher quality
    width: 1920,
    height: 1080,
  } as any)

  return canvas.toDataURL('image/png')
}

/**
 * Capture all slides as images
 */
async function captureAllSlides(slides: SlideData[], onProgress?: (current: number, total: number) => void): Promise<string[]> {
  const container = createExportContainer()
  const screenshots: string[] = []

  try {
    for (let i = 0; i < slides.length; i++) {
      onProgress?.(i + 1, slides.length)

      // Clear previous content
      container.innerHTML = ''

      // Render slide
      const slideElement = renderSlideForExport(slides[i])
      container.appendChild(slideElement)

      try {
        const screenshot = await captureSlideElement(slideElement)
        screenshots.push(screenshot)
      } catch (error) {
        console.error(`Failed to capture slide ${i}:`, error)
        // Create fallback
        screenshots.push(createPlaceholderSlide(i, slides[i]))
      }
    }
  } finally {
    container.remove()
  }

  return screenshots
}

/**
 * Create a placeholder slide image
 */
function createPlaceholderSlide(index: number, slide: SlideData): string {
  const canvas = document.createElement('canvas')
  canvas.width = 1920
  canvas.height = 1080
  const ctx = canvas.getContext('2d')!

  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, 1920, 1080)
  gradient.addColorStop(0, '#667eea')
  gradient.addColorStop(1, '#764ba2')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, 1920, 1080)

  // Text
  ctx.fillStyle = 'white'
  ctx.font = 'bold 64px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  if (slide.title) {
    ctx.fillText(slide.title, 1920 / 2, 1080 / 2 - 60)
  } else {
    ctx.fillText(`Slide ${index + 1}`, 1920 / 2, 1080 / 2 - 60)
  }

  if (slide.subtitle) {
    ctx.font = '40px Arial'
    ctx.fillStyle = 'rgba(255,255,255,0.8)'
    ctx.fillText(slide.subtitle, 1920 / 2, 1080 / 2 + 20)
  }

  ctx.font = '28px Arial'
  ctx.fillStyle = 'rgba(255,255,255,0.6)'
  ctx.fillText(slide.component ? 'Custom component - view in browser for full content' : 'Export preview', 1920 / 2, 1080 / 2 + 100)

  return canvas.toDataURL('image/png')
}

/**
 * Show loading indicator
 */
function showLoadingIndicator(message: string): HTMLElement {
  const loadingEl = document.createElement('div')
  loadingEl.id = 'export-loading-indicator'
  loadingEl.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    color: white;
    font-size: 18px;
    flex-direction: column;
    gap: 16px;
  `
  loadingEl.innerHTML = `
    <div class="spinner" style="width:40px;height:40px;border:3px solid #fff;border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite;"></div>
    <div id="export-loading-message">${message}</div>
    <div id="export-progress-text" style="font-size: 14px; opacity: 0.7;"></div>
  `
  document.body.appendChild(loadingEl)

  const styleEl = document.createElement('style')
  styleEl.id = 'export-spinner-style'
  styleEl.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
  `
  document.head.appendChild(styleEl)

  return loadingEl
}

/**
 * Update loading indicator
 */
function updateLoadingIndicator(loadingEl: HTMLElement, message: string, progress?: string): void {
  const messageEl = loadingEl.querySelector('#export-loading-message')
  const progressEl = loadingEl.querySelector('#export-progress-text')
  if (messageEl) messageEl.textContent = message
  if (progressEl && progress) progressEl.textContent = progress
}

/**
 * Hide loading indicator
 */
function hideLoadingIndicator(loadingEl: HTMLElement): void {
  loadingEl.remove()
  const styleEl = document.getElementById('export-spinner-style')
  if (styleEl) styleEl.remove()
}

/**
 * Export presentation as PDF
 */
export async function exportAsPDF(presentation: PresentationData): Promise<void> {
  const slides = presentation.slides

  if (slides.length === 0) {
    throw new Error('No slides found to export')
  }

  const loadingEl = showLoadingIndicator('Starting PDF export...')

  try {
    // Capture all slides
    updateLoadingIndicator(loadingEl, 'Rendering slides...', `0/${slides.length}`)
    const screenshots = await captureAllSlides(slides, (current, total) => {
      updateLoadingIndicator(loadingEl, 'Rendering slides...', `${current}/${total}`)
    })

    // Create print container
    const printContainer = document.createElement('div')
    printContainer.id = 'export-print-container'
    printContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: white;
      z-index: 9999;
      overflow-y: auto;
      display: none;
    `
    document.body.appendChild(printContainer)

    // Add screenshots
    screenshots.forEach((screenshot, index) => {
      const page = document.createElement('div')
      page.style.cssText = `
        page-break-after: always;
        page-break-inside: avoid;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        background: white;
        ${index === screenshots.length - 1 ? 'page-break-after: auto;' : ''}
      `

      const img = document.createElement('img')
      img.src = screenshot
      img.style.cssText = `
        max-width: 100%;
        max-height: 100vh;
        object-fit: contain;
      `

      page.appendChild(img)
      printContainer.appendChild(page)
    })

    // Add print styles
    const printStyle = document.createElement('style')
    printStyle.id = 'export-print-styles'
    printStyle.textContent = `
      @media print {
        @page {
          size: A4 landscape;
          margin: 0;
        }
        body > *:not(#export-print-container) {
          display: none !important;
        }
        #export-print-container {
          display: block !important;
        }
        #export-print-container > div {
          page-break-after: always;
          page-break-inside: avoid;
        }
        #export-print-container > div:last-child {
          page-break-after: auto;
        }
      }
    `
    document.head.appendChild(printStyle)

    updateLoadingIndicator(loadingEl, 'Opening print dialog...', 'Save as PDF from the print dialog')

    // Show and print
    printContainer.style.display = 'block'
    await new Promise(resolve => setTimeout(resolve, 100))
    window.print()

    // Cleanup
    setTimeout(() => {
      printContainer.remove()
      printStyle.remove()
    }, 1000)

  } catch (error) {
    console.error('PDF export failed:', error)
    throw error
  } finally {
    hideLoadingIndicator(loadingEl)
  }
}

/**
 * Export presentation as standalone HTML
 */
export async function exportAsHTML(presentation: PresentationData): Promise<void> {
  const slides = presentation.slides

  if (slides.length === 0) {
    throw new Error('No slides found to export')
  }

  const loadingEl = showLoadingIndicator('Starting HTML export...')

  try {
    // Capture all slides
    updateLoadingIndicator(loadingEl, 'Rendering slides...', `0/${slides.length}`)
    const screenshots = await captureAllSlides(slides, (current, total) => {
      updateLoadingIndicator(loadingEl, 'Rendering slides...', `${current}/${total}`)
    })

    updateLoadingIndicator(loadingEl, 'Generating HTML file...')

    // Generate HTML
    const htmlContent = generateStandaloneHTML(presentation, screenshots)

    // Download
    const blob = new Blob([htmlContent], { type: 'text/html' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = `${sanitizeFilename(presentation.metadata.title || 'presentation')}.html`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(url)

  } catch (error) {
    console.error('HTML export failed:', error)
    throw error
  } finally {
    hideLoadingIndicator(loadingEl)
  }
}

/**
 * Generate standalone HTML file with embedded slide screenshots
 */
function generateStandaloneHTML(presentation: PresentationData, screenshots: string[]): string {
  const slidesHTML = screenshots.map((src, index) => `
    <div class="slide ${index === 0 ? 'active' : ''}" data-index="${index}">
      <img src="${src}" alt="Slide ${index + 1}" class="slide-image" />
    </div>
  `).join('')

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(presentation.metadata.title)}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html {
      scroll-behavior: smooth;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      background: #000;
      overflow: hidden;
    }

    .presentation-container {
      width: 100vw;
      height: 100vh;
      position: relative;
      overflow: hidden;
    }

    .slide {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.4s ease, transform 0.4s ease;
      transform: translateX(100%);
    }

    .slide.active {
      opacity: 1;
      visibility: visible;
      transform: translateX(0);
      z-index: 10;
    }

    .slide.prev {
      transform: translateX(-100%);
    }

    .slide-image {
      max-width: 100%;
      max-height: 100vh;
      object-fit: contain;
      display: block;
    }

    /* Controls */
    .controls {
      position: fixed;
      bottom: 30px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 12px;
      z-index: 1000;
      background: rgba(255,255,255,0.1);
      backdrop-filter: blur(10px);
      padding: 12px;
      border-radius: 50px;
      border: 1px solid rgba(255,255,255,0.2);
    }

    .control-btn {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      border: none;
      background: rgba(255,255,255,0.1);
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .control-btn:hover:not(:disabled) {
      background: rgba(255,255,255,0.2);
      transform: scale(1.05);
    }

    .control-btn:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .progress-bar {
      position: fixed;
      top: 0;
      left: 0;
      height: 3px;
      background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
      transition: width 0.3s ease;
      z-index: 1000;
    }

    .slide-counter {
      position: fixed;
      bottom: 30px;
      right: 30px;
      font-size: 14px;
      color: rgba(255,255,255,0.5);
      z-index: 1000;
    }

    .keyboard-hint {
      position: fixed;
      bottom: 30px;
      left: 30px;
      font-size: 12px;
      color: rgba(255,255,255,0.3);
      z-index: 1000;
    }

    @media (max-width: 768px) {
      .keyboard-hint {
        display: none;
      }
    }

    @media print {
      .slide {
        position: relative;
        page-break-after: always;
        page-break-inside: avoid;
        opacity: 1 !important;
        visibility: visible !important;
        transform: none !important;
      }
      .controls, .progress-bar, .slide-counter, .keyboard-hint {
        display: none !important;
      }
      @page {
        size: A4 landscape;
        margin: 0;
      }
    }
  </style>
</head>
<body>
  <div class="progress-bar" id="progress"></div>

  <div class="presentation-container">
    ${slidesHTML}
  </div>

  <div class="controls">
    <button class="control-btn" id="prevBtn" title="Previous">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>
    <button class="control-btn" id="nextBtn" title="Next">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>
    <button class="control-btn" id="fullscreenBtn" title="Fullscreen">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
      </svg>
    </button>
    <button class="control-btn" id="printBtn" title="Print">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="6 9 6 2 18 2 18 9"></polyline>
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
        <rect x="6" y="14" width="12" height="8"></rect>
      </svg>
    </button>
  </div>

  <div class="slide-counter">
    <span id="currentSlide">1</span> / <span id="totalSlides">${screenshots.length}</span>
  </div>

  <div class="keyboard-hint">Use arrow keys to navigate • F for fullscreen</div>

  <script>
    (function() {
      const slides = document.querySelectorAll('.slide');
      const totalSlides = slides.length;
      let currentIndex = 0;

      const prevBtn = document.getElementById('prevBtn');
      const nextBtn = document.getElementById('nextBtn');
      const fullscreenBtn = document.getElementById('fullscreenBtn');
      const printBtn = document.getElementById('printBtn');
      const progressBar = document.getElementById('progress');
      const currentSlideEl = document.getElementById('currentSlide');

      function updateSlide() {
        slides.forEach((slide, index) => {
          slide.classList.remove('active', 'prev');
          if (index === currentIndex) {
            slide.classList.add('active');
          } else if (index < currentIndex) {
            slide.classList.add('prev');
          }
        });

        progressBar.style.width = ((currentIndex + 1) / totalSlides * 100) + '%';
        currentSlideEl.textContent = currentIndex + 1;
        prevBtn.disabled = currentIndex === 0;
        nextBtn.disabled = currentIndex === totalSlides - 1;
      }

      function nextSlide() {
        if (currentIndex < totalSlides - 1) {
          currentIndex++;
          updateSlide();
        }
      }

      function prevSlide() {
        if (currentIndex > 0) {
          currentIndex--;
          updateSlide();
        }
      }

      function toggleFullscreen() {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen();
        } else {
          document.exitFullscreen();
        }
      }

      prevBtn.addEventListener('click', prevSlide);
      nextBtn.addEventListener('click', nextSlide);
      fullscreenBtn.addEventListener('click', toggleFullscreen);
      printBtn.addEventListener('click', () => window.print());

      document.addEventListener('keydown', (e) => {
        switch(e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
          case ' ':
            e.preventDefault();
            nextSlide();
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            e.preventDefault();
            prevSlide();
            break;
          case 'Home':
            e.preventDefault();
            currentIndex = 0;
            updateSlide();
            break;
          case 'End':
            e.preventDefault();
            currentIndex = totalSlides - 1;
            updateSlide();
            break;
          case 'f':
          case 'F':
            e.preventDefault();
            toggleFullscreen();
            break;
        }
      });

      let touchStartX = 0;
      document.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      });

      document.addEventListener('touchend', (e) => {
        const diff = touchStartX - e.changedTouches[0].screenX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) nextSlide();
          else prevSlide();
        }
      });

      updateSlide();
    })();
  </script>
</body>
</html>
  `
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Sanitize filename for download
 */
function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[^a-z0-9]/gi, '_')
    .replace(/_+/g, '_')
    .toLowerCase()
    .substring(0, 100)
}
